import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCreateRfi from '../../hooks/useCreateRfi';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getCheckedValuesInObject, getDataRoomRoute } from 'utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DataRoomRFINotificationMedium } from 'router/subrouters/DataRoom/models/DataRoomRfi';

import './RfiCreateTemplate.styles.scss';
import './RfiCreateTemplate.styles.responsive.scss';

type RfiCreateTemplateProps = {
  className?: string;
};

const RfiCreateTemplate: React.FC<RfiCreateTemplateProps> = (props) => {
  const { className } = props;

  const { popSuccess, popError, popServerError } = useContext(
    PopNotificationsContext,
  );
  const { isLoadingTemplates, rfiTemplates, refetchTemplates } =
    useContext(RfiContext);
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);

  const classes = classNames('rfi-create-template', 'white-panel', className);

  const { templateId } = useParams<{ templateId: string }>();

  const template = rfiTemplates.find(({ _id }) => _id === templateId);

  const isEdit = !!template;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { initialValues, RenderSteps } = useCreateRfi(true, template);

  const onSubmit = useCallback(
    async (values) => {
      const {
        documents,
        customDocuments,
        notificationMedium,
        customNote,
        saveAsTemplateName,
      } = values;

      const documentsFinal = [...documents, ...customDocuments]
        .filter(({ checked }) => checked)
        .map(({ name, period }) => ({ name, period }));

      if (!documentsFinal.length) {
        popError(t('chooseAtLeastOneDocument'));
        return;
      }

      const notificationMediumFinal =
        getCheckedValuesInObject(notificationMedium);

      const data = {
        ...(saveAsTemplateName !== template?.name && {
          name: saveAsTemplateName,
        }),
        documents: documentsFinal,
        notifyOnComplete: !!notificationMediumFinal.length,
        notificationMedium:
          notificationMediumFinal as DataRoomRFINotificationMedium[],
        customNote,
      };

      try {
        if (isEdit) {
          await dataRoomApi.rfiTemplates.update(_id, template?._id, data);
        } else {
          await dataRoomApi.rfiTemplates.create(_id, data);
        }

        popSuccess(t(isEdit ? 'templateUpdated' : 'templateCreated'));
        navigate(getDataRoomRoute(_id, 'rfi/list'));
        refetchTemplates();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      _id,
      isEdit,
      template,
      t,
      navigate,
      refetchTemplates,
      popError,
      popSuccess,
      popServerError,
    ],
  );

  if (templateId && !isLoadingTemplates && !template)
    return <Navigate to="../" replace />;

  return (
    <div className={classes}>
      <div className="panel-title">
        <h3>{t(isEdit ? 'editTemplate' : 'createNewTemplate')}</h3>
        <Link to="../list" className="ncoded-button ncoded-button--outline">
          {t('showAll')}
        </Link>
      </div>

      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, pristine }) => (
          <form onSubmit={handleSubmit}>
            {RenderSteps}
            <div className="actions">
              <Button type="submit" disabled={pristine || submitting}>
                {t('saveTemplate')}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default RfiCreateTemplate;
